import { CompanyCredit } from "@stagvista/ui";
import * as React from "react";

/**
 * Where the advert screenshot lives. Drop a PNG/JPG/WebP at
 * `apps/admin-console/public/login-preview.png` (wide, ~1200px) and it shows in the
 * blue panel; until then a drawn placeholder stands in.
 */
const PREVIEW_SRC = "/login-preview.png";

/** Rotating advert lines for the side panel — edit freely. */
const SLIDES: readonly { headline: string; detail: string }[] = [
  {
    headline: "Run every service on time.",
    detail: "Timers, schedules and outputs for the stage, the booth and the back office.",
  },
  {
    headline: "One plan, every screen.",
    detail: "Speaker, audience and operator views stay in step — live.",
  },
  {
    headline: "Plan in the cloud, run it anywhere.",
    detail: "Build the service here; the desktop app keeps it running even offline.",
  },
];

const ROTATE_MS = 6000;

function PreviewPlaceholder(): React.ReactElement {
  return (
    <div
      aria-hidden="true"
      className="w-full rounded-lg border border-white/20 bg-[#0b1220] p-3 shadow-2xl"
    >
      <div className="mb-3 flex gap-1.5">
        <span className="size-2 rounded-full bg-white/30" />
        <span className="size-2 rounded-full bg-white/30" />
        <span className="size-2 rounded-full bg-white/30" />
      </div>
      <div className="mb-3 h-3 w-1/3 rounded bg-white/20" />
      <div className="space-y-2">
        {[0, 1, 2, 3, 4].map((row) => (
          <div key={row} className="flex items-center gap-2">
            <span className="h-2.5 w-10 rounded bg-white/15" />
            <span className="h-2.5 flex-1 rounded bg-white/10" />
            <span className="h-2.5 w-8 rounded bg-brand-primary/70" />
          </div>
        ))}
      </div>
    </div>
  );
}

function AdvertPanel(): React.ReactElement {
  const [slide, setSlide] = React.useState(0);
  const [imageFailed, setImageFailed] = React.useState(false);

  React.useEffect(() => {
    const id = window.setInterval(() => setSlide((n) => (n + 1) % SLIDES.length), ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  const current = SLIDES[slide]!;

  return (
    <div className="relative hidden flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 p-8 text-white lg:flex">
      <div className="pointer-events-none absolute -bottom-16 -left-16 size-56 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -right-10 -top-10 size-40 rounded-full bg-white/10" />

      <div className="relative flex flex-1 items-center">
        {imageFailed ? (
          <PreviewPlaceholder />
        ) : (
          <img
            src={PREVIEW_SRC}
            alt="StagVista app preview"
            onError={() => setImageFailed(true)}
            className="w-full rounded-lg border border-white/20 shadow-2xl"
          />
        )}
      </div>

      <div className="relative mt-8 text-center">
        <p className="text-lg font-semibold leading-snug">{current.headline}</p>
        <p className="mx-auto mt-2 max-w-xs text-body text-white/80">{current.detail}</p>
        <div className="mt-5 flex justify-center gap-2" role="tablist" aria-label="Highlights">
          {SLIDES.map((_, index) => (
            <button
              key={index}
              type="button"
              role="tab"
              aria-selected={index === slide}
              aria-label={`Highlight ${index + 1}`}
              onClick={() => setSlide(index)}
              className={
                index === slide
                  ? "h-2 w-6 rounded-full bg-white"
                  : "size-2 rounded-full bg-white/40 hover:bg-white/70"
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The signed-out screens' shared layout: a white card split in two — an advert panel
 * with an app screenshot on the left (hidden on narrow screens) and the form on the
 * right. Used by sign-in and forgot-password so they always match.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-950 via-blue-900 to-blue-700 p-4 sm:p-8">
      <div className="grid w-full max-w-5xl gap-4 rounded-3xl bg-white p-4 shadow-2xl lg:min-h-[32rem] lg:grid-cols-2">
        <AdvertPanel />
        <div className="flex items-center justify-center px-4 py-8 sm:px-10">
          <div className="w-full max-w-sm text-slate-900">
            <img
              src="/images/logo-light.png"
              alt="StagVista"
              className="mx-auto mb-8 h-12 w-auto"
            />
            <h1 className="text-2xl font-semibold">{title}</h1>
            {subtitle ? <p className="mt-1 text-body text-slate-500">{subtitle}</p> : null}
            <div className="mt-6">{children}</div>
            <CompanyCredit className="mt-8 text-center text-slate-400" />
          </div>
        </div>
      </div>
    </div>
  );
}
